import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram } from 'lucide-react';

const XBrandIcon = ({ className = 'w-4 h-4' }) => (
  <span className={`${className} inline-flex items-center justify-center font-black leading-none`}>X</span>
);

const LinkedInBrandIcon = ({ className = 'w-4 h-4' }) => (
  <span className={`${className} inline-flex items-center justify-center font-black leading-none`}>in</span>
);

const Footer = ({ setActiveTab }) => {
  const handleLegalClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const footerLinks = {
    services: [
      { label: 'AI & Machine Learning', href: '/services/ai' },
      { label: 'SAP Services', href: '/services/sap' },
      { label: 'Cloud & DevOps', href: '/services/cloud-devops' },
      { label: 'Cyber Security', href: '/services/cybersecurity' },
      { label: 'Data Analytics', href: '/services/data-analytics' },
      { label: 'Product Development', href: '/services/product-development' }
    ],
    solutions: [
      { label: 'BPO Services', href: '/solutions/bpo' },
      { label: 'IT Consulting', href: '/solutions/it' },
      { label: 'Strategic Staffing', href: '/solutions/contingent' },
      { label: 'Full-Time Placement', href: '/solutions/fulltime' }
    ]
  };

  return (
    <footer className="bg-primary text-white pt-6 pb-2 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white/5 rounded-full blur-[120px] -mr-40 -mt-40 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-secondary/5 rounded-full blur-[100px] -ml-20 -mb-20 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10 mb-6">
          {/* Brand Column */}
          <div className="space-y-8">
            <Link
              to="/"
              onClick={() => {
                if (setActiveTab) setActiveTab('home');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="flex items-center cursor-pointer group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-primary rounded-sm"
            >
              <img
                src="/top-bar-logo.png"
                alt="Libsys Logo"
                className="h-12 w-auto brightness-0 invert opacity-90 group-hover:opacity-100 transition-opacity"
              />
            </Link>
            <p className="text-blue-100/80 text-[15px] leading-relaxed max-w-sm font-medium">
              Empowering organizations to operate faster, smarter, and with greater agility through future-ready technology solutions and strategic staffing.
            </p>
            <div className="flex items-center space-x-3">
              {[
                { icon: <Facebook className="w-4 h-4" />, href: 'https://www.facebook.com/people/Libsys-Inc/100066298346924/#', hoverBg: 'hover:bg-[#1877F2]' },
                { icon: <XBrandIcon className="w-4 h-4" />, href: 'https://x.com/libsysinc', hoverBg: 'hover:bg-black' },
                { icon: <LinkedInBrandIcon className="w-4 h-4" />, href: 'https://www.linkedin.com/company/libsysinc/', hoverBg: 'hover:bg-[#0A66C2]' },
                { icon: <Instagram className="w-4 h-4" />, href: 'https://www.instagram.com/libsysinc', hoverBg: 'hover:bg-gradient-to-tr hover:from-[#f9ce34] hover:via-[#ee2a7b] hover:to-[#6228d7]' }
              ].map((social, i) => (
                <a
                  key={i}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open social link ${i + 1} in new tab`}
                  className={`w-9 h-9 rounded-full bg-white/8 flex items-center justify-center text-blue-100/90 transition-all duration-300 border border-white/25 ${social.hoverBg} hover:text-white hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-primary`}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Our Services Column */}
          <div>
            <h4 className="text-[14px] font-black mb-4 text-white uppercase tracking-[0.1em]">
              Our Services
            </h4>
            <ul className="space-y-4">
              {footerLinks.services.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-blue-50/95 hover:!text-red-500 text-[16px] font-semibold transition-all flex items-center gap-3 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-primary rounded-sm">
                    <span className="text-white group-hover:!text-red-500 text-[10px] transition-colors">◆</span>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Solutions Column */}
          <div>
            <h4 className="text-[14px] font-black mb-4 text-white uppercase tracking-[0.1em]">
              Solutions
            </h4>
            <ul className="space-y-4">
              {footerLinks.solutions.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-blue-50/95 hover:!text-red-500 text-[16px] font-semibold transition-all flex items-center gap-3 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-primary rounded-sm">
                    <span className="text-white group-hover:!text-red-500 text-[10px] transition-colors">◆</span>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="text-[14px] font-black mb-4 text-white uppercase tracking-[0.1em]">
              Contact Us
            </h4>
            <div className="space-y-6">
              {[
                {
                  label: 'Corporate Office',
                  value: <span className="whitespace-nowrap">2020 Calamos Ct, Suite # 200,<br />Naperville IL 60563, USA</span>,
                  icon: <MapPin className="w-4 h-4" />,
                  link: 'https://www.google.com/maps/search/?api=1&query=2020+Calamos+Ct+Suite+200+Naperville+IL+60563'
                },
                {
                  label: 'Hyderabad Office',
                  value: 'Sanali Spazio, LG Floor, Madhapur, Hyderabad - 500 081',
                  icon: <MapPin className="w-4 h-4" />,
                  link: 'https://www.google.com/maps/search/?api=1&query=Sanali+Spazio+LG+Floor+Madhapur+Hyderabad+500081'
                },
                {
                  label: 'Communication',
                  value: 'Phone: 630-799-1556',
                  icon: <Phone className="w-4 h-4" />,
                  link: 'tel:630-799-1556',
                  isPhone: true
                },
                {
                  label: 'Email Support',
                  value: 'Info@LibsysInc.Com',
                  icon: <Mail className="w-4 h-4" />,
                  link: 'mailto:Info@LibsysInc.Com',
                  isEmail: true
                }
              ].map((item, idx) => (
                <a
                  key={idx}
                  href={item.link}
                  target={item.isEmail || item.isPhone ? undefined : "_blank"}
                  rel={item.isEmail || item.isPhone ? undefined : "noopener noreferrer"}
                  className="flex items-start gap-4 group cursor-pointer rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
                >
                  <div className="w-9 h-9 rounded-lg bg-slate-900/35 border border-slate-300/20 flex items-center justify-center flex-shrink-0 group-hover:border-red-400/70 group-hover:bg-slate-800/45 transition-all">
                    <div className="text-white group-hover:!text-red-400 transition-colors">{item.icon}</div>
                  </div>
                  <div className="pt-0.5">
                    <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-300/70 mb-1 group-hover:!text-red-400 transition-colors">
                      {item.label}
                    </p>
                    <p className="text-blue-50/95 text-subtext font-bold leading-relaxed group-hover:!text-red-400 transition-colors">
                      {item.value}
                    </p>
                    {item.sub && <p className="text-blue-100/65 text-[11px] font-semibold mt-0.5 group-hover:!text-red-400 transition-colors">{item.sub}</p>}
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Elite Bottom Bar */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-6 pt-4 border-t border-white/5 flex flex-col md:flex-row justify-start items-center gap-6 lg:gap-12 relative z-30">
          <p className="text-blue-100/75 text-[11px] font-bold tracking-[0.1em] uppercase">
            © 2026 LIBSYS INC. ALL RIGHTS RESERVED.
          </p>
          <div className="flex flex-wrap justify-start gap-6 lg:gap-10">
            <Link
              to="/terms-of-service"
              onClick={handleLegalClick}
              className="text-blue-100 hover:!text-red-500 text-[11px] font-black uppercase tracking-[0.1em] transition-colors relative z-40 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-primary rounded-sm"
            >
              Terms of Service
            </Link>
            <Link
              to="/privacy-policy"
              onClick={handleLegalClick}
              className="text-blue-100 hover:!text-red-500 text-[11px] font-black uppercase tracking-[0.1em] transition-colors relative z-40 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-primary rounded-sm"
            >
              Privacy Policy
            </Link>
            <Link
              to="/admin"
              onClick={handleLegalClick}
              className="text-blue-100 hover:!text-red-500 text-[11px] font-black uppercase tracking-[0.1em] transition-all relative z-40 cursor-pointer flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-primary rounded-sm"
            >
              Management
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
